/**
 * useHostBookings Hook
 *
 * Fetches and manages host's bookings from Firestore.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { useAuth } from '../../../contexts';

export type BookingStatus = 'pending' | 'confirmed' | 'active' | 'completed' | 'cancelled' | 'declined';

export interface HostBooking {
  id: string;
  listingId: string;
  listingTitle: string;
  listingPhoto: string;
  listingImage: string;
  guest: {
    id: string;
    name: string;
    photo?: string;
  };
  guestName: string;
  guestAvatar?: string;
  checkIn: Date;
  checkOut: Date;
  nights: number;
  guests: {
    adults: number;
    children: number;
    infants: number;
  };
  guestCount: number;
  status: BookingStatus;
  pricing: {
    totalAmount: number;
    hostPayout: number;
    platformCommission: number;
    currency: string;
  };
  totalPrice: number;
  hostPayout: number;
  platformCommission: number;
  createdAt: Date;
  specialRequests?: string;
}

// Helper to safely convert Firestore timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  const d = new Date(value as string | number);
  return isNaN(d.getTime()) ? new Date() : d;
};

export const useHostBookings = (filter?: 'pending' | 'upcoming' | 'past' | 'all') => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<HostBooking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  useEffect(() => {
    if (!user) {
      setBookings([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    async function fetchBookings() {
      try {
        const bookingsRef = collection(db, 'bookings');
        const q = query(
          bookingsRef,
          where('hostId', '==', user!.uid),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);

        if (cancelled) return;

        // Collect unique guest IDs to batch-fetch guest profiles
        const guestIds = new Set<string>();
        const rawBookings: Array<{ id: string; data: Record<string, unknown> }> = [];

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          rawBookings.push({ id: docSnap.id, data });
          if (data.guestId) guestIds.add(data.guestId as string);
        });

        // Fetch guest profiles (Firestore "in" queries support up to 30 items)
        const guestMap = new Map<string, { displayName: string; photoURL?: string }>();
        const guestIdArray = Array.from(guestIds);

        for (let i = 0; i < guestIdArray.length; i += 30) {
          const batch = guestIdArray.slice(i, i + 30);
          const usersRef = collection(db, 'users');
          const usersQuery = query(usersRef, where('__name__', 'in', batch));
          const usersSnap = await getDocs(usersQuery);

          usersSnap.forEach((userDoc) => {
            const userData = userDoc.data();
            guestMap.set(userDoc.id, {
              displayName: userData.displayName || 'Guest',
              photoURL: userData.photoURL || undefined,
            });
          });
        }

        if (cancelled) return;

        const mapped: HostBooking[] = rawBookings.map(({ id, data }) => {
          const guestId = (data.guestId as string) || '';
          const guestProfile = guestMap.get(guestId);
          const guestName = guestProfile?.displayName || 'Guest';
          const guestPhoto = guestProfile?.photoURL || undefined;

          const listingSnapshot = data.listing as
            | { title?: string; photo?: string; location?: string }
            | undefined;

          const pricing = data.pricing as
            | {
                totalAmount?: number;
                hostPayout?: number;
                platformCommission?: number;
                currency?: string;
              }
            | undefined;

          const guests = (data.guests as { adults: number; children: number; infants: number }) || {
            adults: 1,
            children: 0,
            infants: 0,
          };

          const listingTitle = listingSnapshot?.title || 'Listing';
          const listingPhoto = listingSnapshot?.photo || '';

          return {
            id,
            listingId: (data.listingId as string) || '',
            listingTitle,
            listingPhoto,
            listingImage: listingPhoto,
            guest: {
              id: guestId,
              name: guestName,
              photo: guestPhoto,
            },
            guestName,
            guestAvatar: guestPhoto,
            checkIn: toDate(data.checkIn),
            checkOut: toDate(data.checkOut),
            nights: (data.nights as number) || 1,
            guests,
            guestCount: guests.adults + guests.children,
            status: (data.status as BookingStatus) || 'pending',
            pricing: {
              totalAmount: pricing?.totalAmount || 0,
              hostPayout: pricing?.hostPayout || 0,
              platformCommission: pricing?.platformCommission || 0,
              currency: pricing?.currency || 'USD',
            },
            totalPrice: pricing?.totalAmount || 0,
            hostPayout: pricing?.hostPayout || 0,
            platformCommission: pricing?.platformCommission || 0,
            createdAt: toDate(data.createdAt),
            specialRequests: (data.specialRequests as string) || undefined,
          };
        });

        setBookings(mapped);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching host bookings:', err);
          setError(err instanceof Error ? err : new Error('Failed to fetch bookings'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchBookings();

    return () => {
      cancelled = true;
    };
  }, [user, fetchKey]);

  // Filter bookings
  const filteredBookings = useMemo(() => {
    const now = new Date();

    switch (filter) {
      case 'pending':
        return bookings.filter((b) => b.status === 'pending');
      case 'upcoming':
        return bookings.filter(
          (b) => b.status === 'confirmed' && b.checkIn > now
        );
      case 'past':
        return bookings.filter(
          (b) => b.status === 'completed' || b.checkOut < now
        );
      default:
        return bookings;
    }
  }, [bookings, filter]);

  const pendingCount = bookings.filter((b) => b.status === 'pending').length;
  const upcomingCount = bookings.filter(
    (b) => b.status === 'confirmed' && b.checkIn > new Date()
  ).length;

  return {
    bookings: filteredBookings,
    isLoading,
    error,
    refetch,
    counts: { pending: pendingCount, upcoming: upcomingCount },
  };
};

export default useHostBookings;
